import { useSyncExternalStore } from 'react';

// A store for tracking committed (already in DB) vs pending (newly selected) jobs/activities.
// Keys format: "J:{clientJobId}" or "A:{clientJobId}:{activityId}"
class SelectionStore {
  constructor() {
    this.committed = new Map(); // key -> tsJobId
    this.pending = new Set();   // keys newly picked this session
    this.listeners = new Set();
  }

  // Hydrate the store from the current timesheet jobs (committed rows)
  hydrateCommittedLegacy(selectJobData) {
    this.committed.clear();
    this.pending.clear();
    if (selectJobData?.jobs1) {
      selectJobData.jobs1.forEach(j => {
        this.committed.set(`J:${j.clientJobID}`, j.timesheetJobID);
      });
    }
    if (selectJobData?.Act) {
      selectJobData.Act.forEach(a => {
        // We might not have clientJobId in Act natively if it's only actid. 
        // We'll need to rely on the A:{jobId}:{actId} if possible, or just A:{actId}.
        // The modal uses `act.id === item2.actid` to map it, which means `item2` doesn't strictly need jobId to match if actid is globally unique, but standard is `A:{actId}` for legacy.
        this.committed.set(`A:${a.actid}`, a.timesheetJobID);
      });
    }
    this.emitChange();
  }

  getMode(key, altKey) {
    if (this.committed.has(key) || (altKey && this.committed.has(altKey))) return 'committed';
    if (this.pending.has(key)) return 'pending';
    return undefined;
  }

  toggle(key) {
    if (this.committed.has(key)) {
      // Typically tapping a committed row initiates a delete flow, which shouldn't happen via simple toggle.
      // Handled at the component level.
      return;
    }

    if (this.pending.has(key)) {
      this.pending.delete(key);
    } else {
      this.pending.add(key);
    }
    this.emitChange();
  }

  removeCommitted(key) {
    this.committed.delete(key);
    this.emitChange();
  }

  resetPending() {
    this.pending.clear();
    this.emitChange();
  }

  getPendingKeys() {
    return Array.from(this.pending);
  }

  hasPending() {
    return this.pending.size > 0;
  }

  hasAnySelection() {
    return this.committed.size > 0 || this.pending.size > 0;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emitChange() {
    for (let listener of this.listeners) {
      listener();
    }
  }
}

export const selectionStore = new SelectionStore();

export const useHasPending = () => {
  return useSyncExternalStore(
    (cb) => selectionStore.subscribe(cb),
    () => selectionStore.hasPending()
  );
};

export const useHasAnySelection = () => {
  return useSyncExternalStore(
    (cb) => selectionStore.subscribe(cb),
    () => selectionStore.hasAnySelection()
  );
};

export const useSelectionMode = (key, altKey) => {
  return useSyncExternalStore(
    (cb) => selectionStore.subscribe(cb),
    () => selectionStore.getMode(key, altKey)
  );
};

